import cc from 'classcat';
import PropTypes from 'prop-types';
import { memo, useEffect, useState, useRef } from 'react';

import css from './index.module.scss';
import Notch from '../Notch';
import XLarge from '../icons/XLarge';
import { KEY_CODES, KEY_DOWN, FOCUSABLE_ELEMENTS } from '../../config';

const Alert = ({ cancel, children, up }) => {
  const root = useRef(null);
  const [mounted, setMounted] = useState(false);

  const moduleClass = cc([css.root, { [css.rootUp]: up }]);
  const bgClass = cc([css.bg, { [css.bgUp]: up }]);

  useEffect(() => {
    const dialog = root.current;

    if (!dialog || !up) {
      return;
    }

    // add all the elements which you want to make focusable
    const focusableContent = dialog.querySelectorAll(FOCUSABLE_ELEMENTS);
    const first = focusableContent[0];
    const last = focusableContent[focusableContent.length - 1];

    const onKeyDown = (event) => {
      const isTabPressed = event.key === KEY_CODES.TAB;

      if (event.key === KEY_CODES.ESC) {
        cancel();
        return;
      }

      if (!isTabPressed) {
        return;
      }

      if (event.shiftKey) {
        // if shift key pressed for shift + tab combination
        if (document.activeElement === first) {
          last.focus(); // add focus for the last focusable element
          event.preventDefault();
        }
      } else {
        // if tab key is pressed
        // eslint-disable-next-line no-lonely-if
        if (document.activeElement === last) {
          // if focused has reached to last focusable element then focus first focusable element after pressing tab
          first.focus();
          event.preventDefault();
        }
      }
    };
    document.removeEventListener(KEY_DOWN, onKeyDown);
    document.addEventListener(KEY_DOWN, onKeyDown);
    setTimeout(() => first.focus(), 400);
  }, [up, mounted]);

  useEffect(() => {
    let timeout = null;
    if (!mounted) {
      timeout = setTimeout(() => setMounted(true), 50);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [mounted]);

  return (
    <>
      <div className={moduleClass} ref={root}>
        <Notch />
        <button aria-label="Cancel" className={css.close} onClick={cancel} tabIndex={up ? 0 : -1} type="button">
          <XLarge />
        </button>
        {children}
      </div>
      <button aria-label="Cancel" className={bgClass} onClick={cancel} tabIndex={up ? 0 : -1} type="button" />
    </>
  );
};

Alert.propTypes = {
  cancel: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.node]).isRequired,
  up: PropTypes.bool.isRequired,
};

export default memo(Alert);
